import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FileText, Trash2, Sparkles, TrendingUp, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { loadBudgets, deleteBudget, calcTotals, fmtEUR, fmtDate } from "../lib/storage";

export default function Dashboard() {
  const [budgets, setBudgets] = useState([]);
  const [q, setQ] = useState("");

  const refresh = () => setBudgets(loadBudgets());
  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return budgets;
    return budgets.filter(
      (b) =>
        b.id.toLowerCase().includes(term) ||
        (b.cliente?.nombre || "").toLowerCase().includes(term)
    );
  }, [budgets, q]);

  const stats = useMemo(() => {
    const totals = budgets.map((b) => calcTotals(b.partidas).total);
    return {
      count: budgets.length,
      sum: totals.reduce((a, n) => a + n, 0),
      avg: budgets.length ? totals.reduce((a, n) => a + n, 0) / budgets.length : 0,
    };
  }, [budgets]);

  const handleDelete = (id) => {
    if (confirm("¿Eliminar este presupuesto?")) {
      deleteBudget(id);
      refresh();
    }
  };

  return (
    <div className="px-10 py-10 max-w-7xl mx-auto" data-testid="dashboard">
      <div className="flex items-end justify-between mb-2">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
            Reformas Express
          </div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight" data-testid="dashboard-title">
            Tus presupuestos.
          </h1>
          <p className="text-slate-500 mt-2 max-w-xl">
            Crea, edita y exporta presupuestos de reforma con asistencia IA. Todo se guarda en este navegador (datos mock).
          </p>
        </div>
        <Link to="/nuevo" data-testid="dashboard-new-btn">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-5 rounded-md gap-2">
            <Plus className="w-4 h-4" /> Nuevo presupuesto
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-10 mb-8">
        <StatCard label="Presupuestos" value={stats.count} icon={<FileText className="w-5 h-5" />} testid="stat-count" />
        <StatCard
          label="Volumen total"
          value={fmtEUR(stats.sum)}
          icon={<TrendingUp className="w-5 h-5" />}
          testid="stat-sum"
        />
        <StatCard
          label="Ticket medio"
          value={fmtEUR(stats.avg)}
          icon={<Sparkles className="w-5 h-5" />}
          testid="stat-avg"
        />
      </div>

      <div className="rx-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
          <h2 className="font-display font-bold text-lg">Listado</h2>
          <div className="relative w-72 max-w-full">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por ID o cliente…"
              className="pl-9 h-9"
              data-testid="dashboard-search"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="dashboard-empty">
            <div className="inline-flex p-4 rounded-full bg-blue-50 text-blue-600 mb-4">
              <FileText className="w-7 h-7" />
            </div>
            <h3 className="font-display font-bold text-xl mb-2">Sin presupuestos aún</h3>
            <p className="text-slate-500 mb-5 text-sm">
              Crea tu primer presupuesto en menos de 1 minuto con la asistencia IA.
            </p>
            <Link to="/nuevo">
              <Button className="bg-blue-600 hover:bg-blue-700 gap-2">
                <Sparkles className="w-4 h-4" /> Probar con IA
              </Button>
            </Link>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map((b) => {
              const total = calcTotals(b.partidas).total;
              return (
                <div
                  key={b.id}
                  className="px-5 py-4 grid grid-cols-12 gap-4 items-center hover:bg-slate-50/60 transition-colors"
                  data-testid={`budget-row-${b.id}`}
                >
                  <div className="col-span-3">
                    <div className="font-mono text-xs text-slate-400">{b.id}</div>
                    <div className="font-semibold text-slate-900 truncate">
                      {b.cliente?.nombre || "Sin cliente"}
                    </div>
                  </div>
                  <div className="col-span-3 text-sm text-slate-600 truncate">
                    {b.cliente?.direccion || "—"}
                  </div>
                  <div className="col-span-2 text-sm text-slate-500">{fmtDate(b.fecha)}</div>
                  <div className="col-span-1">
                    <span
                      className={`text-xs px-2 py-1 rounded-md font-semibold ${
                        b.estado === "final"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {b.estado === "final" ? "Final" : "Borrador"}
                    </span>
                  </div>
                  <div className="col-span-2 text-right font-display font-bold text-blue-600">
                    {fmtEUR(total)}
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    <Link to={`/presupuesto/${b.id}`} data-testid={`budget-edit-${b.id}`}>
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-600 hover:text-blue-600">
                        Abrir
                      </Button>
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(b.id)}
                      data-testid={`budget-delete-${b.id}`}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      aria-label="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, testid }) {
  return (
    <div className="rx-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 text-slate-400">
        <span className="text-xs uppercase tracking-widest font-bold">{label}</span>
        {icon}
      </div>
      <div className="font-display font-bold text-2xl tracking-tight">{value}</div>
    </div>
  );
}
